import { useState } from 'react';
import type { OrderDto, OrderEventDto, OrderStatus } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { dateTime, money } from '@/lib/format';
import { describeEvent } from '@/lib/order-timeline';
import { Section } from '@/components/page';
import { Button, Textarea, cx } from '@/components/ui';
import {
  AlertIcon,
  CartIcon,
  ChatIcon,
  CheckIcon,
  CoinsIcon,
  KeyIcon,
  HomeIcon,
  MailIcon,
  PencilIcon,
  PhoneIcon,
  ReturnIcon,
  TruckIcon,
  UsersIcon,
} from '@/components/icons';

/**
 * What happened to the order, newest first, with a place to write on it
 * (ADR-059) — the shape the reference dashboards share: the system's moments
 * and the team's notes in one line, so "who changed this and why?" and "did
 * the customer get the email?" are both answered here.
 *
 * Who acted is a name, not an id. What changed is said in words: from which
 * status to which, the note left with it, whether the customer was told,
 * whether the stock went back. The money, returns, and orders the team placed
 * or changed are worded by `describeEvent` (ADR-061, ADR-062).
 */
export function OrderTimeline({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { addNote, updating } = useOrdersStore();
  const [draft, setDraft] = useState('');

  const events = [...(order.events ?? [])].reverse();

  async function submit() {
    const note = draft.trim();
    if (!note) return;
    if (await addNote(order.id, note)) setDraft('');
  }

  return (
    <Section title={t('timeline')} subtitle={t('orderNotesStaffOnly')} flush>
      <form
        className="flex flex-col gap-2 border-t border-line px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Textarea
          aria-label={t('orderNotePlaceholder')}
          placeholder={t('orderNotePlaceholder')}
          rows={2}
          maxLength={1000}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // ⌘/Ctrl + Enter posts, the way every comment box does.
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={updating || draft.trim() === ''}>
            {t('addNote')}
          </Button>
        </div>
      </form>

      <ol className="relative flex flex-col border-t border-line px-5 py-4">
        {events.map((event, index) => (
          <TimelineRow
            key={`${event.createdAt}-${index}`}
            event={event}
            isPickup={order.isPickup}
            currency={order.totals.currency}
            last={index === events.length - 1}
            locale={locale}
          />
        ))}
      </ol>
    </Section>
  );
}

function TimelineRow({
  event,
  isPickup,
  currency,
  last,
  locale,
}: {
  event: OrderEventDto;
  isPickup: boolean;
  currency: string;
  last: boolean;
  locale: 'ar' | 'en';
}) {
  const t = useT();
  const statusLabel = useStatusLabel();
  const data = event.data ?? {};
  const isNote = event.type === 'order.note';
  const failed = event.type === 'email.failed' || event.type === 'payment.failed' || event.type === 'payment.refund_failed';

  const Icon = ICONS[event.type] ?? CheckIcon;

  const details: string[] = [];
  if (event.type === 'order.status_changed') {
    if (typeof data.notified === 'boolean') details.push(data.notified ? t('eventNotified') : t('eventNotNotified'));
    if (data.to === 'CANCELLED' && typeof data.restocked === 'boolean') {
      details.push(data.restocked ? t('eventRestocked') : t('eventNotRestocked'));
    }
    if (typeof data.carrier === 'string') {
      details.push([data.carrier, typeof data.trackingNumber === 'string' ? data.trackingNumber : null].filter(Boolean).join(' · '));
    }
  }
  if ((event.type === 'email.sent' || event.type === 'email.failed') && typeof data.template === 'string') {
    const name = NOTIFICATION_NAMES[data.template];
    details.push(name ? t(name) : t(`mail.${data.template}` as MessageKey));
  }

  // The words a person wrote: a note, or the reason left with a status change.
  let written = isNote
    ? (typeof data.note === 'string' ? data.note : '')
    : typeof data.reason === 'string'
      ? data.reason
      : '';

  // Everything else — cash collected, money paid outside the store, a refund and
  // how it went back, a return, an order placed or changed by the team.
  if (!WORDED_HERE.includes(event.type)) {
    const line = describeEvent(event, {
      t,
      money: (halalas) => money(halalas, locale, currency),
      status: (status) => statusLabel(status as OrderStatus, isPickup),
      returnStatus: (status) => t(`returnStatus${status}` as MessageKey),
    });
    if (line.detail) details.push(line.detail);
    if (!written && line.quote) written = line.quote;
  }

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {!last ? <span aria-hidden className="absolute start-[15px] top-8 bottom-0 w-px bg-line" /> : null}
      <span
        aria-hidden
        className={cx(
          'relative z-10 inline-flex size-8 shrink-0 items-center justify-center rounded-full border',
          isNote
            ? 'border-brand-500/30 bg-brand-50 text-brand-700'
            : failed
              ? 'border-danger/30 bg-danger/10 text-danger'
              : 'border-line bg-surface-sunken text-ink-muted',
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 pt-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className={cx('text-sm', failed ? 'text-danger' : 'text-ink')}>
            {event.type === 'order.status_changed' && typeof data.to === 'string' ? (
              typeof data.from === 'string' ? (
                t('eventFromTo', {
                  from: statusLabel(data.from as OrderStatus, isPickup),
                  to: statusLabel(data.to as OrderStatus, isPickup),
                })
              ) : (
                t('eventTo', { to: statusLabel(data.to as OrderStatus, isPickup) })
              )
            ) : (
              t(`event.${event.type}`)
            )}
          </p>
          <p className="text-xs text-ink-muted">
            <ActorName event={event} /> · <time dateTime={event.createdAt}>{dateTime(event.createdAt, locale)}</time>
          </p>
        </div>
        {written ? (
          <p
            className={cx(
              'mt-1.5 whitespace-pre-line text-sm',
              // A staff note reads as a message: a tinted bubble, square at the corner nearest its icon.
              isNote ? 'rounded-panel rounded-ss-sm bg-brand-50 px-3.5 py-2.5 text-ink ring-1 ring-brand-500/15' : 'text-ink-muted',
            )}
          >
            {written}
          </p>
        ) : null}
        {details.length > 0 ? <p className="mt-1 text-xs text-ink-muted">{details.join(' · ')}</p> : null}
        {event.type === 'order.address_changed' ? <AddressChange data={data} /> : null}
      </div>
    </li>
  );
}

/** A person by their name; the store's machinery by what it is. */
function ActorName({ event }: { event: OrderEventDto }) {
  const t = useT();
  if (event.actorName) return <>{event.actorName}</>;
  const known = ACTORS[event.actor];
  if (known) return <>{t(known)}</>;
  // An id whose account is gone — a colleague who has left.
  return <>{t('actorFormerStaff')}</>;
}

/** The street before and after, so a correction can be checked against what the customer said. */
function AddressChange({ data }: { data: Record<string, unknown> }) {
  const locale = useUiStore((state) => state.locale);
  const line = (value: unknown) => {
    if (typeof value !== 'object' || value === null) return '';
    const address = value as Record<string, unknown>;
    return [address.line1, address.line2, address.district, address.shortCode, address.phone]
      .filter((part) => typeof part === 'string' && part !== '')
      .join(locale === 'ar' ? '، ' : ', ');
  };
  return (
    <div className="mt-1.5 flex flex-col gap-0.5 text-xs">
      <span className="text-ink-faint line-through">{line(data.before)}</span>
      <span className="text-ink-muted">{line(data.after)}</span>
    </div>
  );
}

/** The moments this row words itself; the rest are `describeEvent`'s. */
const WORDED_HERE: OrderEventDto['type'][] = ['order.status_changed', 'order.note', 'order.address_changed', 'email.sent', 'email.failed'];

const ACTORS: Record<string, MessageKey> = {
  system: 'actor_system',
  webhook: 'actor_webhook',
  guest: 'actor_guest',
};

const NOTIFICATION_NAMES: Record<string, MessageKey> = {
  'order.placed.cod': 'notify_order.placed',
  'order.placed.unpaid': 'notify_order.placed',
  'order.paid': 'notify_order.paid',
  'order.shipped': 'notify_order.shipped',
  'order.ready_for_pickup': 'notify_order.ready_for_pickup',
  'order.cancelled': 'notify_order.cancelled',
  'order.digital': 'notify_order.digital',
};

const ICONS: Partial<Record<OrderEventDto['type'], (props: { className?: string }) => React.JSX.Element>> = {
  'order.placed': CartIcon,
  'order.status_changed': TruckIcon,
  'payment.succeeded': CoinsIcon,
  'payment.from_balance': CoinsIcon,
  'payment.failed': AlertIcon,
  'payment.refunded': CoinsIcon,
  'return.requested': ReturnIcon,
  'return.status_changed': ReturnIcon,
  'email.sent': MailIcon,
  'email.failed': AlertIcon,
  'sms.sent': PhoneIcon,
  'order.claimed': UsersIcon,
  'order.note': ChatIcon,
  'order.address_changed': HomeIcon,
  'order.edited': PencilIcon,
  'payment.refund_failed': AlertIcon,
  'payment.refund_unconfirmed': AlertIcon,
  'order.assigned': UsersIcon,
  'digital.delivered': KeyIcon,
  'digital.short': AlertIcon,
};
